import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Divider from '@mui/material/Divider';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import useAuth from '../hooks/useAuth';
import { getReviewQueue, claimAssessment } from '../services/mentorApi';
import { formatDate } from '../utils/formatters';
import StatusChip from '../components/common/StatusChip';

const AUTO_REFRESH_MS = 30000;

export default function ReviewQueuePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [claimingId, setClaimingId] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const intervalRef = useRef(null);

  const loadQueue = useCallback(() => {
    if (!user?.mentor_id) return Promise.resolve();
    return getReviewQueue(user.mentor_id, 50)
      .then((data) => {
        setQueue(data?.queue || []);
        setLastRefreshed(new Date());
        setError('');
      })
      .catch((err) => setError(err?.response?.data?.error || err.message));
  }, [user]);

  useEffect(() => {
    setLoading(true);
    loadQueue().finally(() => setLoading(false));
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => loadQueue(), AUTO_REFRESH_MS);
    return () => clearInterval(intervalRef.current);
  }, [loadQueue]);

  const handleClaim = async (assessmentId) => {
    setClaimingId(assessmentId);
    try {
      await claimAssessment(assessmentId, user.mentor_id);
      navigate(`/queue/${assessmentId}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setSnack({
          open: true,
          message: 'Another mentor claimed this one seconds before you. Refreshing the queue.',
          severity: 'warning',
        });
        loadQueue();
      } else {
        setSnack({
          open: true,
          message: err?.response?.data?.error || err.message || 'Failed to claim',
          severity: 'error',
        });
      }
    } finally {
      setClaimingId(null);
    }
  };

  const poolItems = queue.filter((q) => q.status === 'pending_review');
  const myClaims = queue.filter((q) => q.status === 'under_review');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>Review Queue</Typography>
          <Typography variant="body2" color="text.secondary">
            Claim an assessment to lock it for review. You have 24 hours from claim time to finish.
            {lastRefreshed && ` Last refreshed ${formatDate(lastRefreshed.toISOString())}.`}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => loadQueue()}
        >
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* My active claims */}
          {myClaims.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LockOutlinedIcon fontSize="small" />
                Your Claimed Reviews ({myClaims.length})
              </Typography>
              <Grid container spacing={2}>
                {myClaims.map((item) => (
                  <QueueCard
                    key={item.assessment_id}
                    item={item}
                    variant="claimed"
                    onAction={() => navigate(`/queue/${item.assessment_id}`)}
                  />
                ))}
              </Grid>
              <Divider sx={{ my: 3 }} />
            </>
          )}

          {/* Shared pool */}
          <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
            Shared Pool ({poolItems.length})
          </Typography>
          {poolItems.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No assessments waiting.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Check back later — auto-refreshes every 30 seconds.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {poolItems.map((item) => (
                <QueueCard
                  key={item.assessment_id}
                  item={item}
                  variant="pool"
                  claiming={claimingId === item.assessment_id}
                  onAction={() => handleClaim(item.assessment_id)}
                />
              ))}
            </Grid>
          )}
        </>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function QueueCard({ item, variant, claiming = false, onAction }) {
  const slaMs = item.sla_expires_at ? item.sla_expires_at * 1000 - Date.now() : null;
  const slaHoursLeft = slaMs != null ? slaMs / 3600000 : null;
  const slaWarning = slaHoursLeft != null && slaHoursLeft < 4;
  const isClaimed = variant === 'claimed';

  return (
    <Grid item xs={12} md={6}>
      <Paper sx={{ p: 3, borderLeft: isClaimed ? '3px solid' : undefined, borderColor: isClaimed ? 'primary.main' : undefined }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {(item.service_type || 'assessment').replace(/^./, c => c.toUpperCase())} Assessment
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {item.assessment_id?.slice(0, 8)}…
            </Typography>
          </Box>
          <StatusChip status={item.status} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
          <Typography variant="body2">
            <strong>Intern:</strong> {item.intern_id?.slice(0, 8) || '—'}…
          </Typography>
          <Typography variant="body2">
            <strong>Scheduled:</strong> {formatDate(item.scheduled_time)}
          </Typography>
          <Typography variant="body2">
            <strong>Submitted:</strong> {formatDate(item.submitted_at)}
          </Typography>
          {isClaimed && slaHoursLeft != null && (
            <Typography variant="body2" sx={{ color: slaWarning ? 'error.main' : 'text.primary', fontWeight: slaWarning ? 600 : 400 }}>
              <AccessTimeIcon fontSize="inherit" sx={{ verticalAlign: 'text-bottom', mr: 0.5 }} />
              <strong>SLA:</strong> {slaHoursLeft > 0 ? `${slaHoursLeft.toFixed(1)}h remaining` : 'EXPIRED — at risk of auto-release'}
            </Typography>
          )}
        </Box>

        {item.has_critical_findings && (
          <Chip
            icon={<WarningIcon />}
            label="Critical findings"
            size="small"
            color="error"
            sx={{ mb: 2 }}
          />
        )}

        <Button
          variant="contained"
          color={isClaimed ? 'primary' : 'success'}
          startIcon={isClaimed ? <OpenInNewIcon /> : <LockOutlinedIcon />}
          onClick={onAction}
          disabled={claiming}
          fullWidth
        >
          {claiming ? <CircularProgress size={20} color="inherit" /> : isClaimed ? 'Open Review' : 'Claim Review (24h SLA)'}
        </Button>
      </Paper>
    </Grid>
  );
}
